/**
 * Fase 3 Blokk 3E — Kandidat opplasting av prosjekt-innlevering
 *
 * Rute: /submissions/:submissionId
 *
 * Kandidat må være Vipps-innlogget (cookie-session).
 * Side viser:
 *   - Komponent-info + frist (nedtelling)
 *   - Veiledning
 *   - Drag-and-drop opplastings-område
 *   - Liste over opplastede filer (med fjern-knapp inntil submit)
 *   - KI-samtykke-toggle (hvis komponent krever)
 *   - "Finaliser og send inn"-knapp
 */

import { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Upload, FileText, Trash2, CheckCircle, AlertCircle, Clock, ArrowLeft } from 'lucide-react';

interface SubmissionFile {
  name: string;
  size_bytes: number;
  mime_type: string;
  index: number;
}

interface SubmissionDetail {
  id: string;
  status: string;
  file_names: string[] | null;
  file_sizes_bytes: number[] | null;
  file_mime_types: string[] | null;
  file_virus_scan_status: string | null;
  ai_consent_status: string | null;
  component_title: string;
  component_description: string | null;
  component_type: string;
  deadline: string | null;
  grace_period_hours: number;
  project_allowed_extensions: string[];
  project_max_file_size_mb: number;
  max_files_per_submission: number;
  course_title?: string;
}

export default function SubmissionUploadPage() {
  const { submissionId } = useParams<{ submissionId: string }>();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [submission, setSubmission] = useState<SubmissionDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [finalizingAt, setFinalizingAt] = useState<string | null>(null);
  const [acceptAiConsent, setAcceptAiConsent] = useState<boolean | null>(null);

  const sessionToken = typeof window !== 'undefined' ? localStorage.getItem('vipps_session_token') : null;

  const loadSubmission = useCallback(async () => {
    if (!submissionId || !sessionToken) return;
    try {
      setLoading(true);
      const res = await fetch(`/api/candidate/assessment-submissions/${submissionId}`, {
        headers: { 'X-Vipps-Session': sessionToken }
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setSubmission(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'load_failed');
    } finally {
      setLoading(false);
    }
  }, [submissionId, sessionToken]);

  useEffect(() => { loadSubmission(); }, [loadSubmission]);

  const handleFileSelected = async (file: File | null | undefined) => {
    if (!file || !submission) return;
    setError(null);
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await fetch(`/api/candidate/assessment-submissions/${submissionId}/upload`, {
        method: 'POST',
        headers: { 'X-Vipps-Session': sessionToken || '' },
        body: form
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || `HTTP ${res.status}`);
      }
      await loadSubmission();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'upload_failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRemoveFile = async (index: number) => {
    if (!submission || !submissionId) return;
    if (!confirm('Fjerne denne filen? Du kan laste opp en ny etterpå.')) return;
    try {
      const res = await fetch(`/api/candidate/assessment-submissions/${submissionId}/files/${index}?session_token=${encodeURIComponent(sessionToken || '')}`, {
        method: 'DELETE',
        headers: { 'X-Vipps-Session': sessionToken || '' }
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await loadSubmission();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'remove_failed');
    }
  };

  const handleFinalize = async () => {
    if (!submission || !submissionId) return;
    if (!(submission.file_names && submission.file_names.length > 0)) {
      setError('no_files_to_submit');
      return;
    }
    if (submission.component_type === 'project' && acceptAiConsent === null) {
      setError('consent_choice_required');
      return;
    }
    setFinalizingAt(new Date().toISOString());
    try {
      // Oppdater samtykke via register-instance hvis endret
      // (NB: dette er tilfeldigvis det eneste stedet samtykke oppdateres i Fase 3 MVP)
      const res = await fetch(`/api/candidate/assessment-submissions/${submissionId}/finalize`, {
        method: 'POST',
        headers: {
          'X-Vipps-Session': sessionToken || '',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ session_token: sessionToken })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      await loadSubmission();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'finalize_failed');
    } finally {
      setFinalizingAt(null);
    }
  };

  if (loading) return <div className="p-8">Laster inn innlevering...</div>;
  if (!submission) {
    return (
      <div className="max-w-3xl mx-auto p-8">
        <h1 className="text-2xl font-bold mb-4">Innlevering ikke funnet</h1>
        {error && <p className="text-red-600">{error}</p>}
        <Link to="/dashboard" className="text-kf-blue-600 underline">Tilbake til Min side</Link>
      </div>
    );
  }

  const files: SubmissionFile[] = (submission.file_names || []).map((name, i) => ({
    name,
    size_bytes: submission.file_sizes_bytes?.[i] || 0,
    mime_type: submission.file_mime_types?.[i] || '',
    index: i
  }));

  const isLocked = submission.status === 'submitted' || submission.status === 'graded';
  const atMaxFiles = files.length >= submission.max_files_per_submission;

  const deadlineMs = submission.deadline ? new Date(submission.deadline).getTime() : null;
  const effectiveCutoff = deadlineMs
    ? new Date(deadlineMs + (submission.grace_period_hours || 0) * 3600 * 1000)
    : null;
  const nowMs = Date.now();
  const timeLeftHours = effectiveCutoff
    ? Math.max(0, Math.round((effectiveCutoff.getTime() - nowMs) / 3600 / 1000))
    : null;
  const deadlinePassed = effectiveCutoff ? nowMs > effectiveCutoff.getTime() : false;

  return (
    <div className="max-w-3xl mx-auto p-8 space-y-6">
      <Link to="/dashboard" className="inline-flex items-center text-kf-blue-600 hover:underline">
        <ArrowLeft className="w-4 h-4 mr-1" /> Tilbake til Min side
      </Link>

      <header>
        <h1 className="text-2xl font-bold text-kf-blue-900">{submission.component_title}</h1>
        {submission.course_title && (
          <p className="text-sm text-gray-600 mt-1">{submission.course_title}</p>
        )}
      </header>

      {submission.component_description && (
        <section className="bg-gray-50 rounded-lg p-4">
          <h2 className="font-semibold mb-2">Veiledning</h2>
          <div className="prose prose-sm whitespace-pre-line">{submission.component_description}</div>
        </section>
      )}

      {submission.deadline && (
        <div className={`flex items-center p-4 rounded-lg ${deadlinePassed ? 'bg-red-50 text-red-800' : 'bg-amber-50 text-amber-900'}`}>
          <Clock className="w-5 h-5 mr-2" />
          <div>
            <div className="font-semibold">
              Frist: {new Date(submission.deadline).toLocaleString('nb-NO', { timeZone: 'Europe/Oslo', dateStyle: 'full', timeStyle: 'short' })}
            </div>
            {deadlinePassed
              ? <div className="text-sm">Frist har utløpt (inkludert grace-periode på {submission.grace_period_hours}t).</div>
              : <div className="text-sm">{timeLeftHours}t igjen (inkl. grace-periode {submission.grace_period_hours}t).</div>
            }
          </div>
        </div>
      )}

      <section className="border rounded-lg p-4">
        <h2 className="font-semibold mb-3">Opplastede filer ({files.length} / {submission.max_files_per_submission})</h2>
        {files.length === 0 ? (
          <p className="text-sm text-gray-500">Ingen filer lastet opp enda.</p>
        ) : (
          <ul className="divide-y">
            {files.map(f => (
              <li key={f.index} className="flex items-center justify-between py-2">
                <div className="flex items-center">
                  <FileText className="w-5 h-5 mr-2 text-kf-blue-600" />
                  <div>
                    <div className="font-medium">{f.name}</div>
                    <div className="text-xs text-gray-500">
                      {(f.size_bytes / 1024 / 1024).toFixed(1)} MB · {f.mime_type}
                    </div>
                  </div>
                </div>
                {!isLocked && (
                  <button onClick={() => handleRemoveFile(f.index)}
                    className="text-red-600 hover:text-red-800 p-2"
                    aria-label="Fjern fil">
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}

        {!isLocked && !deadlinePassed && !atMaxFiles && (
          <div className="mt-4">
            <input
              type="file"
              ref={fileInputRef}
              className="hidden"
              accept={submission.project_allowed_extensions.map(e => `.${e}`).join(',')}
              onChange={e => handleFileSelected(e.target.files?.[0])}
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="inline-flex items-center px-4 py-2 bg-kf-blue-600 text-white rounded-lg hover:bg-kf-blue-700 disabled:opacity-50"
            >
              <Upload className="w-4 h-4 mr-2" />
              {uploading ? 'Laster opp (virus-skanning kjører)...' : 'Last opp fil'}
            </button>
            <p className="text-xs text-gray-500 mt-2">
              Tillatt: {submission.project_allowed_extensions.join(', ')} · Maks {submission.project_max_file_size_mb} MB per fil
            </p>
          </div>
        )}
        {atMaxFiles && !isLocked && (
          <p className="text-sm text-amber-700 mt-3">Du har nådd maks antall filer. Fjern en fil for å laste opp en annen.</p>
        )}
      </section>

      {!isLocked && submission.component_type === 'project' && files.length > 0 && (
        <section className="border rounded-lg p-4 bg-purple-50">
          <h2 className="font-semibold mb-2">KI-forhåndsvurdering (valgfritt)</h2>
          <p className="text-sm text-gray-700 mb-3">
            Vi kan la en KI gi sensor et <strong>forslag til vurdering</strong>.
            Dokumentet pseudonymiseres før det sendes. Sensor har alltid endelig ord.
            Du kan takke nei — da får sensor en ren manuell vurdering.
          </p>
          <div className="space-y-2">
            <label className="flex items-center">
              <input type="radio" name="ai_consent" checked={acceptAiConsent === true}
                onChange={() => setAcceptAiConsent(true)} className="mr-2" />
              Ja, bruk KI-forhåndsvurdering som støtte for sensor
            </label>
            <label className="flex items-center">
              <input type="radio" name="ai_consent" checked={acceptAiConsent === false}
                onChange={() => setAcceptAiConsent(false)} className="mr-2" />
              Nei, kun manuell vurdering
            </label>
          </div>
        </section>
      )}

      {error && (
        <div className="bg-red-50 text-red-800 p-3 rounded-lg flex items-start">
          <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0" />
          <div>
            <div className="font-semibold">Feil</div>
            <div className="text-sm">{error}</div>
          </div>
        </div>
      )}

      {isLocked ? (
        <div className="bg-green-50 text-green-800 p-4 rounded-lg flex items-start">
          <CheckCircle className="w-6 h-6 mr-2 flex-shrink-0" />
          <div>
            <div className="font-semibold">Levert</div>
            <div className="text-sm">Din innlevering er mottatt. Du får beskjed når sensor har vurdert.</div>
          </div>
        </div>
      ) : (
        <button
          onClick={handleFinalize}
          disabled={files.length === 0 || finalizingAt !== null || deadlinePassed}
          className="w-full inline-flex items-center justify-center px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
        >
          <CheckCircle className="w-5 h-5 mr-2" />
          {finalizingAt ? 'Sender inn...' : 'Finaliser og send inn'}
        </button>
      )}
    </div>
  );
}
